import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../store/sessionStore';
import { useAuthStore } from '../store/authStore';
import { wsService } from '../services/websocket';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  ChevronDownIcon,
  PlayIcon,
  StopIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const OBSPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const {
    sessions,
    chatters,
    isLoading,
    fetchSessions,
    updateSession,
    setCurrentSession,
    fetchChatters,
    addChatter,
    updateChatter,
    toggleChatterGreeted,
  } = useSessionStore();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Persistent collapse state
  const [sessionMonitorCollapsed, setSessionMonitorCollapsed] = useState(() => {
    const stored = localStorage.getItem('obsSessionMonitorCollapsed');
    return stored ? JSON.parse(stored) : false;
  });
  useEffect(() => {
    localStorage.setItem('obsSessionMonitorCollapsed', JSON.stringify(sessionMonitorCollapsed));
  }, [sessionMonitorCollapsed]);

  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [hideGreeted, setHideGreeted] = useState(() => {
    const saved = localStorage.getItem('hideGreeted');
    return saved ? JSON.parse(saved) : false;
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const chattersContainerRef = React.useRef<HTMLDivElement>(null);
  const templateRowRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(10);

  const selectedSession = sessions.find(s => s.id === selectedSessionId);
  
  const filteredChatters = chatters
    .filter(chatter => !hideGreeted || !chatter.isGreeted)
    .sort((a, b) => new Date(a.firstSeenAt).getTime() - new Date(b.firstSeenAt).getTime());

  // Dynamically calculate how many chatters fit based on actual row height
  useEffect(() => {
    function updateVisibleCount() {
      if (chattersContainerRef.current && templateRowRef.current) {
        const containerHeight = chattersContainerRef.current.offsetHeight;
        const rowHeight = templateRowRef.current.offsetHeight || 48; // fallback
        const bottomMargin = 70;
        const count = Math.max(1, Math.floor((containerHeight - bottomMargin) / rowHeight));
        setVisibleCount(count);
      }
    }
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [filteredChatters.length]);

  const maxVisible = Math.max(1, visibleCount);
  const visibleChatters = filteredChatters.slice(0, maxVisible);
  const moreCount = filteredChatters.length - maxVisible;

  // Render a hidden template row for measurement
  const templateChatter = filteredChatters[0] || {
    id: 'template',
    displayName: 'Mock User',
    profileImage: null,
    isGreeted: false,
    firstSeenAt: new Date().toISOString(),
    username: '',
    twitchId: '',
    messageCount: 1,
    greetedAt: null,
    greetedBy: null,
    lastSeenAt: new Date().toISOString(),
    firstMessage: '',
  };

  // Reset to page 1 when filters or page size change
  useEffect(() => {
    if (sessions.length > 0) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [sessions]);

  useEffect(() => {
    if (!sessions.length) {
      fetchSessions();
    }
  }, [fetchSessions, sessions.length]);

  // Listen for session-related WebSocket events instead of polling
  useEffect(() => {
    const handleSessionStarted = (payload: any) => {

      // Refresh sessions when a session is started
      fetchSessions();
    };

    const handleSessionStopped = () => {

      // Refresh sessions when a session is stopped
      fetchSessions();
    };

    const handleSessionCreated = (payload: any) => {

      // Refresh sessions when a new session is created
      fetchSessions();
    };

    const handleSessionUpdated = (payload: any) => {

      // Refresh sessions when a session is updated
      fetchSessions();
    };

    const handleSessionDeleted = (payload: any) => {

      // Refresh sessions when a session is deleted
      fetchSessions();
    };

    // Add WebSocket listeners for session events
    wsService.on('session_started', handleSessionStarted);
    wsService.on('session_stopped', handleSessionStopped);
    wsService.on('session_created', handleSessionCreated);
    wsService.on('session_updated', handleSessionUpdated);
    wsService.on('session_deleted', handleSessionDeleted);

    return () => {
      wsService.off('session_started', handleSessionStarted);
      wsService.off('session_stopped', handleSessionStopped);
      wsService.off('session_created', handleSessionCreated);
      wsService.off('session_updated', handleSessionUpdated);
      wsService.off('session_deleted', handleSessionDeleted);
    };
  }, [fetchSessions]);

  // Gentle refresh mechanism - only when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Only refresh when page becomes visible again
        fetchSessions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSessions]);

  useEffect(() => {
    // Auto-select first active session or first session
    if (sessions.length > 0 && !selectedSessionId) {
      const activeSession = sessions.find(s => s.isActive);
      const sessionToSelect = activeSession || sessions[0];
      setSelectedSessionId(sessionToSelect.id);
    }
    
    // If there's a new active session and the current selected session is inactive,
    // switch to the new active session
    if (sessions.length > 0 && selectedSessionId) {
      const currentSelected = sessions.find(s => s.id === selectedSessionId);
      const activeSession = sessions.find(s => s.isActive);
      
      if (activeSession && currentSelected && !currentSelected.isActive && activeSession.id !== selectedSessionId) {
        setSelectedSessionId(activeSession.id);
      }
    }
  }, [sessions, selectedSessionId]);

  useEffect(() => {
    if (selectedSession) {
      setCurrentSession(selectedSession);
      fetchChatters(selectedSession.id);
      
      // Join the session to receive real-time updates
      wsService.joinSession(selectedSession.id);
    }
  }, [selectedSession, setCurrentSession, fetchChatters]);

  useEffect(() => {
    // WebSocket event listeners
    const handleNewChatter = (chatter: any) => {
      addChatter(chatter);
    };

    const handleChatterUpdate = (chatter: any) => {
      updateChatter(chatter);
    };

    wsService.on('new_chatter', handleNewChatter);
    wsService.on('chatter_update', handleChatterUpdate);

    return () => {
      wsService.off('new_chatter', handleNewChatter);
      wsService.off('chatter_update', handleChatterUpdate);
    };
  }, [addChatter, updateChatter]);

  const handleSessionChange = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setDropdownOpen(false);
  };

  // Helper functions to handle session keywords as JSON string
  const parseSessionKeywords = (jsonString: string): string[] => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  // Helper function to check if current user is session owner
  const isSessionOwner = (session: any) => {
    return session.userId === user?.id;
  };

  const handleStartSession = async () => {
    if (!selectedSession || !user) return;

    try {
      await updateSession(selectedSession.id, { isActive: true });
      wsService.startSession(selectedSession.id, parseSessionKeywords(selectedSession.keywords));
    } catch (error: any) {
      if (error.response?.data?.error === 'Only session owners can start sessions') {
        toast.error('Only session owners can start sessions. Editors can view and manage chatters.');
      } else {
        console.error('Failed to start session');
        toast.error('Failed to start session');
      }
    }
  };

  const handleStopSession = async () => {
    if (!selectedSession) return;

    try {
      await updateSession(selectedSession.id, { isActive: false });
      wsService.stopSession();
    } catch (error) {
      console.error('Failed to stop session');
    }
  };

  const handleToggleGreet = async (chatterId: string, isGreeted: boolean) => {
    await toggleChatterGreeted(chatterId, isGreeted);
  };

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  const totalCount = chatters.length;
  const greetedCount = chatters.filter(c => c.isGreeted).length;
  const progress = totalCount > 0 ? Math.round((greetedCount / totalCount) * 100) : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-twitch-600"></div>
      </div>
    );
  }

  return (
    <div className="obs-page-container flex flex-col h-screen overflow-hidden bg-transparent font-sans text-sm px-2" style={{ overflow: 'hidden', height: '100vh' }}>
      {/* Header with Session Selector */}
      <div className="bg-black light:bg-white bg-opacity-90 light:bg-opacity-90 rounded-lg p-2 mb-2 backdrop-blur-sm border border-gray-700 light:border-gray-300">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-base font-bold text-white light:text-gray-900">Waddup.me OBS</h1>
            <p className="text-xs text-gray-400 light:text-gray-600">Session Monitor</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-lg font-bold text-twitch-400">{progress}%</div>
              <div className="text-xs text-gray-400 light:text-gray-600">
                {greetedCount}/{totalCount} greeted
              </div>
            </div>
            <button
              onClick={() => setSessionMonitorCollapsed((prev: boolean) => !prev)}
              className="p-1 rounded text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-200 transition-colors"
              title={sessionMonitorCollapsed ? "Expand Session Monitor" : "Collapse Session Monitor"}
            >
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${sessionMonitorCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Session Selector - Collapsible */}
        {!sessionMonitorCollapsed && (
          <>
            <div className="relative mb-3">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`w-full border rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-700 light:hover:bg-gray-200 transition-colors flex items-center justify-between ${
                  selectedSession?.isActive 
                    ? 'bg-green-900 light:bg-green-100 bg-opacity-20 light:bg-opacity-20 border-green-600 light:border-green-500 text-green-100 light:text-green-800' 
                    : 'bg-gray-800 light:bg-gray-100 border-gray-600 light:border-gray-300 text-white light:text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="truncate">
                    {selectedSession ? selectedSession.name : 'Select Session'}
                  </span>
                  {selectedSession?.isActive && (
                    <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full font-medium">
                      LIVE
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {selectedSession?.isActive && (
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  )}
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 light:text-gray-600" />
                </div>
              </button>
              
              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 light:bg-white border border-gray-600 light:border-gray-300 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                  {sessions.length === 0 ? (
                    <div className="px-3 py-2 text-gray-400 light:text-gray-600 text-xs text-center">
                      No sessions available
                    </div>
                  ) : (
                    sessions.map((session) => (
                    <button
                      key={session.id}
                      onClick={() => handleSessionChange(session.id)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700 light:hover:bg-gray-200 transition-colors flex items-center justify-between ${
                        session.isActive 
                          ? 'bg-green-900 light:bg-green-100 bg-opacity-20 light:bg-opacity-20 border-l-2 border-green-400 light:border-green-500 text-green-100 light:text-green-800' 
                          : 'text-white light:text-gray-900'
                      } ${
                        selectedSessionId === session.id 
                          ? 'bg-twitch-900 light:bg-twitch-100 bg-opacity-30 light:bg-opacity-30' 
                          : ''
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="truncate">{session.name}</span>
                        {session.isActive && (
                          <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full font-medium">
                            LIVE
                          </span>
                        )}
                      </div>
                      {session.isActive && (
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      )}
                    </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Session Controls */}
            {selectedSession && (
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedSession.isActive
                      ? 'bg-green-900 light:bg-green-100 text-green-300 light:text-green-800 border border-green-700 light:border-green-300'
                      : 'bg-gray-700 light:bg-gray-200 text-gray-300 light:text-gray-700 border border-gray-600 light:border-gray-300'
                  }`}>
                    {selectedSession.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {/* Only show start/stop button for session owners */}
                {isSessionOwner(selectedSession) && (
                  <button
                    onClick={selectedSession.isActive ? handleStopSession : handleStartSession}
                    className={`flex items-center px-3 py-1 rounded text-xs font-medium transition-colors ${
                      selectedSession.isActive
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-twitch-600 hover:bg-twitch-700 text-white'
                    }`}
                  >
                    {selectedSession.isActive ? (
                      <>
                        <StopIcon className="h-3 w-3 mr-1" />
                        Stop
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-3 w-3 mr-1" />
                        Start
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Progress Bar */}
            <div className="w-full bg-gray-700 light:bg-gray-300 rounded-full h-1.5">
              <div
                className="bg-twitch-600 h-1.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="bg-black light:bg-white bg-opacity-90 light:bg-opacity-90 rounded-lg p-2 text-center backdrop-blur-sm border border-gray-700 light:border-gray-300">
          <div className="text-lg font-bold text-white light:text-gray-900">{totalCount}</div>
          <div className="text-xs text-gray-400 light:text-gray-600">Total</div>
        </div>
        <div className="bg-black light:bg-white bg-opacity-90 light:bg-opacity-90 rounded-lg p-2 text-center backdrop-blur-sm border border-gray-700 light:border-gray-300">
          <div className="text-lg font-bold text-green-400 light:text-green-600">{greetedCount}</div>
          <div className="text-xs text-gray-400 light:text-gray-600">Greeted</div>
        </div>
        <div className="bg-black light:bg-white bg-opacity-90 light:bg-opacity-90 rounded-lg p-2 text-center backdrop-blur-sm border border-gray-700 light:border-gray-300">
          <div className="text-lg font-bold text-yellow-400 light:text-yellow-600">{totalCount - greetedCount}</div>
          <div className="text-xs text-gray-400 light:text-gray-600">Pending</div>
        </div>
      </div>

      {/* Recent Chatters */}
      <div className="flex-1 min-h-0 flex flex-col bg-black light:bg-white bg-opacity-90 light:bg-opacity-90 rounded-lg p-2 backdrop-blur-sm border border-gray-700 light:border-gray-300" style={{ marginBottom: '70px' }}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-300 light:text-gray-700 flex items-center">
            <UserGroupIcon className="h-4 w-4 mr-1" />
            People To Greet ({filteredChatters.length})
          </h2>
          <div className="flex items-center space-x-2">
            <label className="flex items-center text-xs">
              <input
                type="checkbox"
                checked={hideGreeted}
                onChange={(e) => {
                  setHideGreeted(e.target.checked);
                  localStorage.setItem('hideGreeted', JSON.stringify(e.target.checked));
                }}
                className="h-3 w-3 text-twitch-600 bg-gray-700 light:bg-gray-200 border-gray-600 light:border-gray-300 rounded focus:ring-twitch-500 focus:ring-1 mr-1"
              />
              <span className="text-gray-400 light:text-gray-600">Hide greeted</span>
            </label>
          </div>
        </div>
        
        <div
          className="flex-1 min-h-0 space-y-1.5 hide-scrollbar"
          ref={chattersContainerRef}
        >
          {/* Hidden template row for measurement */}
          <div
            ref={templateRowRef}
            style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none', height: 'auto' }}
            className={`flex items-center justify-between p-2 rounded transition-all duration-200 ${
              templateChatter.isGreeted
                ? 'bg-green-900 light:bg-green-100 bg-opacity-30 light:bg-opacity-30 border border-green-700 light:border-green-300'
                : 'bg-gray-800 light:bg-gray-100 bg-opacity-50 light:bg-opacity-50 border border-gray-700 light:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 rounded-full overflow-hidden bg-gray-600 light:bg-gray-300 flex items-center justify-center">
                  <span className="text-xs font-medium text-white light:text-gray-700">M</span>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-white light:text-gray-900 truncate">Mock User</p>
                <p className="text-xs text-gray-400 light:text-gray-600 truncate">just now</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-2">
              <button className="w-6 h-6 rounded flex items-center justify-center text-xs font-medium transition-all duration-200 bg-gray-600 light:bg-gray-300 text-gray-300 light:text-gray-700">
                <span>⏳</span>
              </button>
            </div>
          </div>
          {/* Render only as many chatters as fit */}
          {visibleChatters.map((chatter) => (
            <div
              key={chatter.id}
              className={`flex items-center justify-between p-2 rounded transition-all duration-200 ${
                chatter.isGreeted
                  ? 'bg-green-900 light:bg-green-100 bg-opacity-30 light:bg-opacity-30 border border-green-700 light:border-green-300'
                  : 'bg-gray-800 light:bg-gray-100 bg-opacity-50 light:bg-opacity-50 border border-gray-700 light:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 rounded-full overflow-hidden bg-gray-600 light:bg-gray-300 flex items-center justify-center">
                    {chatter.profileImage ? (
                      <img 
                        src={chatter.profileImage} 
                        alt={chatter.displayName} 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-medium text-white light:text-gray-700">
                        {chatter.displayName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-white light:text-gray-900 truncate">
                    {chatter.displayName}
                  </p>
                  <p className="text-xs text-gray-400 light:text-gray-600 truncate">
                    {formatDistanceToNow(new Date(chatter.firstSeenAt), { addSuffix: true })}
                    {chatter.isGreeted && chatter.greetedByUser && (
                      <span className="text-green-400 light:text-green-600">
                        {' • by '}{chatter.greetedByUser.displayName}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 ml-2">
                <button
                  onClick={() => handleToggleGreet(chatter.id, !chatter.isGreeted)}
                  className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium transition-all duration-200 ${
                    chatter.isGreeted
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-600 light:bg-gray-300 text-gray-300 light:text-gray-700 hover:bg-gray-500 light:hover:bg-gray-400 hover:text-white light:hover:text-gray-900'
                  }`}
                >
                  {chatter.isGreeted ? <span>😊</span> : <span>⏳</span>}
                </button>
              </div>
            </div>
          ))}
          {moreCount > 0 && (
            <div className="flex items-center justify-center text-xs text-gray-400 light:text-gray-600 py-2">
              + {moreCount} more
            </div>
          )}
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        
        /* Ensure no scrollbars on OBS page specifically */
        .obs-page-container {
          overflow: hidden !important;
          height: 100vh !important;
        }
        
        /* Hide scrollbars on OBS page */
        .obs-page-container::-webkit-scrollbar { display: none; }
        .obs-page-container { -ms-overflow-style: none; scrollbar-width: none; }
        
        /* Target the entire OBS page and its parents */
        .obs-page-container,
        .obs-page-container *,
        main:has(.obs-page-container),
        main:has(.obs-page-container) * {
          overflow: hidden !important;
        }
        
        /* Alternative approach for browsers that don't support :has() */
        .obs-page-container {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          overflow: hidden !important;
        }
      `}</style>
    </div>
  );
};

export default OBSPage; 